import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Activity, Database, ListFilter, Shield, Users } from 'lucide-react';
import { useSession } from '@generated/components/session';
import { UsersPanel } from '@generated/components/UsersPanel';
import { ActivityPanel } from '@generated/components/ActivityPanel';
import { getOptions } from '@generated/server/decisions';

export const Route = createFileRoute('/_app/settings')({ component: SettingsPage });

const SECTIONS = [
  { group: 'System Settings', items: [['security', 'Security', Shield], ['boards', 'Connected Boards', Database], ['activity', 'Activity Log', Activity]] },
  { group: 'User and Account Management', items: [['users', 'User Accounts', Users]] },
  { group: 'Filter Options', items: [['filters', 'Filter Options', ListFilter]] },
] as const;
type Key = 'security' | 'boards' | 'activity' | 'users' | 'filters';

function SettingsPage() {
  const { user } = useSession();
  const [tab, setTab] = useState<Key>('users');
  if (user?.role !== 'admin') return <p className="glass-card rounded-2xl p-8 text-center text-sm text-muted-foreground">Settings are available to administrators only.</p>;
  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      <aside className="glass-card h-fit rounded-2xl p-3">
        {SECTIONS.map((s) => (
          <div key={s.group} className="mb-3">
            <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{s.group}</p>
            {s.items.map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)} className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm ${tab === k ? 'bg-primary/10 font-semibold text-primary' : 'text-foreground/80 hover:bg-muted'}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </div>
        ))}
      </aside>
      <div className="min-w-0">
        {tab === 'users' && <UsersPanel />}
        {tab === 'activity' && <ActivityPanel />}
        {tab === 'security' && <Info title="Security" lines={['Sign-in requires a username plus a private access token, stored only as a salted SHA-256 hash and compared in constant time.', 'Accounts lock after 5 failed attempts; an administrator can unlock them or reset the token.', 'Sessions end after 30 minutes of inactivity.', 'Roles, library scopes and permissions are enforced on the server for every request.']} />}
        {tab === 'boards' && <Info title="Connected Boards" lines={['ITP Hockey Canada Precedents — published when a file is in “Published Decision”.', 'Sport Dispute Precedents — published when a file is in “Published Decision”.', 'Removing the file unpublishes the decision everywhere in the app.']} />}
        {tab === 'filters' && <FilterOptions />}
      </div>
    </div>
  );
}

function Info({ title, lines }: { title: string; lines: string[] }) {
  return (
    <section className="glass-card rounded-2xl p-6">
      <h2 className="text-xl font-semibold text-secondary">{title}</h2>
      <ul className="mt-4 space-y-2 text-sm text-foreground/80">{lines.map((l) => <li key={l} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{l}</li>)}</ul>
    </section>
  );
}

function FilterOptions() {
  const { sid } = useSession();
  const [opts, setOpts] = useState<Record<string, string[]> | null>(null);
  useEffect(() => { getOptions({ data: { sid, lib: 'all' } }).then((r) => setOpts(r?.options ?? {})).catch((e) => { console.error(e); setOpts({}); }); }, [sid]);
  return (
    <section className="glass-card rounded-2xl p-6">
      <h2 className="text-xl font-semibold text-secondary">Filter Options</h2>
      <p className="mt-1 text-sm text-muted-foreground">Read-only: labels come from the board columns. Edit labels on the monday boards.</p>
      {!opts ? <div className="mt-4 h-32 animate-pulse rounded-xl bg-muted" /> : (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {Object.entries(opts).filter(([, v]) => v.length).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-border p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-secondary">{k.replace(/([A-Z])/g, ' $1')}</p>
              <div className="mt-2 flex max-h-32 flex-wrap gap-1 overflow-y-auto">{v.map((x) => <span key={x} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">{x}</span>)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
